import React from "react";

import { PropTypes } from "prop-types";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";

import { AccountingDayRouting as AccountingDayRoutingModal } from "components/AccountingDayRouting";
import { EditReceivalLotModal } from "components/ArrivalScanning/ReceivalLot/Modal";
import { ReceivalLotsTable } from "components/ArrivalScanning/Table";
import { ArrivalScanningView } from "components/ArrivalScanning/View";
import { FeatureRoute } from "components/Authorization/FeatureRoute";
import SecurityTrimmedRoute from "components/Authorization/SecurityTrimmedRoute";
import { BillingWorkFlow } from "components/BillingWorkFlow";
import { BulkUpdateBusinessModal } from "components/BulkUpdateOptionalFieldsModal/Businesses";
import { BulkUpdateSaleLotsModal } from "components/BulkUpdateOptionalFieldsModal/SaleLots";
import { BulkUpdateClearingSaleSaleLotsModal } from "components/BulkUpdateOptionalFieldsModal/ClearingSaleLots";
import { BusinessesInSaleTable } from "components/Business/BusinessesInSale/Table";
import { BusinessPaymentsModal } from "components/BusinessPaymentsModal/Modal";
import BusinessUserSaleLots from "components/BusinessUserSaleLots";
import { BuyerPaymentSummaryModal } from "components/BuyerPaymentSummaryModal/Modal";
import { EditCheckpointModalAdapter } from "components/Checkpoints/Modal";
import { Buyers, Vendors } from "components/ClearingSaleCards";
import { Catalogue } from "components/ClearingSaleCards/Catalogue";
import CommentsModalAdapter from "components/Comments/Modal/adapter";
import ConsignmentCardsView from "components/ConsignmentCardsView";
import ConsignmentsTable from "components/ConsignmentsTable";
import DeliverSaleLotModalAdapter from "components/DeliverSaleLotModal/Adapter";
import DeliveryPenModalAdapter from "components/DeliveryPenModal/Adapter";
import DeliveryPenOwnersModalAdapter from "components/DeliveryPens/Modal/adapter";
import { EContractView } from "components/EContracts";
import EditBidderModalAdapter from "components/EditBidderModal/Adapter";
import { EditPaymentModal } from "components/EditPaymentModal/Modal";
import EditScanningPenModalAdapter from "components/EditScanningPenModal/Adapter";
import { HashRoute } from "components/HashRoute";
import {
  AuctionsPlusImporter,
  ExternalAgentXmlImporter,
  PreSaleCsvImporter,
  SG8Importer,
} from "components/Importer";
import { ClearingSalePreSaleCsvImporter } from "components/Importer/PreSaleCsv";
import InlineCreateSaleLotModalAdapter from "components/InlineCreateSaleLotModal/adapter";
import KillSheetModal from "components/KillSheet";
import MergeSaleLotModal from "components/MergeSaleLotModal/Adapter";
import { MLAPenList } from "components/MLAPenCardList";
import { StateModalAdapter } from "components/Modal/DispatchingAdapter";
import { NominationCards } from "components/Nominations/Cards";
import { NominationTable } from "components/Nominations/Table";
import {
  LotSheet,
  RenderA3PlacardV3,
  RenderA4PlacardV3,
  RenderPlacardV1,
  RenderPlacardV2,
  RenderPlacardV4,
  RenderPlacardV5,
  SheepPlacard,
} from "components/Pdfs";
import { SheepPenStoreCard } from "components/Pdfs/SheepPenStoreCard";
import { AllocatePenScanLotsModal } from "components/PenScanning/AllocationModal";
import EditPenScanLotModalAdapter from "components/PenScanning/PenScanLot/Modal/Adapter";
import { PenScanLotsTable } from "components/PenScanning/Table";
import { PenScanningView } from "components/PenScanning/View";
import { HooksSale, PaddockSale } from "components/PrivateSales";
import PrivateSaleSaleLotsTable from "components/PrivateSales/SaleLotsTable";
import { SaleLotModalAdapter } from "components/SaleLotForms/Adapter";
import SaleLotsTable from "components/SaleLotsTable";
import { SaleProgressDetailsDialog } from "components/SaleProgress/SaleProgressDetailsDialog";
import { SaleWatcher } from "components/SaleWatcher";
import Scan from "components/ScanningScreen/Adapter";
import ScanTable from "components/ScanTable";
import ScanToPenModalAdapter from "components/ScanToPenModal/Adapter";
import {
  ConsignmentCarrierChargeModalAdapter,
  SaleLotCarrierChargeModalAdapter,
} from "components/SelectCarrierChargeModal";
import { SendVendorReportsModal } from "components/SendVendorReportsModal";
import SplitSaleLotModal from "components/SplitLotModal/Adapter";
import { SundryModalAdapter } from "components/Sundry/Form/Adapter";
import { ManualAdjustmentTableModal } from "components/Sundry/Table";
import VendorSplitConsignmentModalAdapter from "components/TransactionSplits/VendorSplitConsignmentModal/adapter";
import VendorSplitSaleLotModalAdapter from "components/TransactionSplits/VendorSplitSaleLotModal/adapter";
import UpdateBuyerModalAdapter from "components/UpdateBuyerModal/adapter";
import { UpdateDefaultPropertyModal } from "components/UpdateDefaultPropertyModal";
import { ImportWeighLotsModal } from "components/WeighLots/Modal";
import { WeighScanLotsTable } from "components/WeighScanning";
import { WeightSettingsModal } from "components/WeighScanning/SettingsModal";

import { ModalTypes } from "constants/navigation";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { SaleType } from "constants/sale";

import AuctionPenCardsView from "containers/AuctionPenCardsView";
import AuctionPenSale from "containers/AuctionPenSale";
import BuyerCardsView from "containers/BuyerCardsView";
import EditConsignmentModal from "containers/Consignment";
import Attachments from "containers/Dashboard/Attachments";
import { ExternalAgentSaleOverview } from "containers/Dashboard/ExternalAgentSaleOverview";
import Overview from "containers/Dashboard/Overview";
import Reports from "containers/Dashboard/Reports";
import ScaleOperatorOverview from "containers/Dashboard/ScaleOperatorOverview";
import { NLISFiles } from "containers/NLISFiles";
import { ConfirmManualTransfer } from "containers/NLISFiles/ConfirmManualTransferModal";
import RegisteredBiddersView from "containers/RegisteredBiddersView";
import EditSaleLotModal from "containers/SaleLot/Adapter";
import Sell from "containers/Sell";
import Take from "containers/Take";
import WeighbridgeSaleViewRoute from "containers/WeighbridgeSaleView";

import {
  BusinessUserSaleLotsSaleEntryRoute,
  getClearingSaleCsvImportRoute,
  getSaleEntryRoute,
  getSaleyardAuctionRoute,
  SaleWatcherSaleEntryRoute,
} from "lib/navigation";

import {
  getCurrentSaleSubTypeIsMlaEnabled,
  getCurrentSaleyard,
  getIsBusinessUser,
  getIsLivestockAgent,
  getIsSaleWatcher,
  selectCurrentDeployments,
  selectRoleCurrentDeployments,
} from "selectors";

import { useHasMLAPermission, useHasSaleyardPermission } from "hooks";
import { SaleLotCardsView } from "components/SaleLotCardsView";

const SaleTypeOverviewComponentMap = {
  [SaleType.BOBBYCALF]: ScaleOperatorOverview,
  [SaleType.CLEARING]: Overview,
  [SaleType.EXPORT]: Overview,
  [SaleType.ON_FARM_AUCTION]: Overview,
  [SaleType.OVER_HOOKS]: HooksSale,
  [SaleType.PADDOCK]: PaddockSale,
  [SaleType.SALEYARD_AUCTION]: Overview,
  [SaleType.SIGHTING]: Overview, // This is not yet implemented
  [SaleType.EXTERNAL_AGENT_SALE]: ExternalAgentSaleOverview,
  [SaleType.SELL_VIA_EXTERNAL_AGENT]: ExternalAgentSaleOverview,
};

function getSaleTypeOverviewComponent(saleType) {
  return SaleTypeOverviewComponentMap[saleType] || Overview;
}

const SaleLotsTableComponentMap = {
  [SaleType.OVER_HOOKS]: PrivateSaleSaleLotsTable,
  [SaleType.PADDOCK]: PrivateSaleSaleLotsTable,
};

function getSaleLotsTableComponent(saleType) {
  return SaleLotsTableComponentMap[saleType] || SaleLotsTable;
}

export function SaleRoutes(props) {
  const { saleId, saleType, saleyardName, saleyardId } = props;
  const isBusinessUser = useSelector(getIsBusinessUser);
  const isSaleWatcher = useSelector(getIsSaleWatcher);
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const hasMlaPermission = useHasMLAPermission();
  const currentSaleSubTypeMlaEnabled = useSelector(
    getCurrentSaleSubTypeIsMlaEnabled,
  );

  const mlaTabIsVisible = hasMlaPermission && currentSaleSubTypeMlaEnabled;

  const hasOverviewPermission = useHasSaleyardPermission(
    SaleyardPermission.featureOverview,
  );

  const sendToMlaTab = mlaTabIsVisible && !hasOverviewPermission;

  const saleyard = useSelector(getCurrentSaleyard);
  const allowLivestockAgentNlisRoute =
    saleyard?.isAgentManagedNlisYard && isLivestockAgent;

  const salePath = getSaleyardAuctionRoute(":saleyard", ":saleId");

  let defaultRoute = null;
  if (isBusinessUser) {
    defaultRoute = BusinessUserSaleLotsSaleEntryRoute;
  } else if (isSaleWatcher) {
    defaultRoute = SaleWatcherSaleEntryRoute;
  }
  const defaultRedirect = getSaleEntryRoute(
    ":saleyard",
    ":saleId",
    saleType,
    defaultRoute,
    sendToMlaTab,
  );

  if (isBusinessUser) {
    return (
      <Switch>
        <Route path={`${salePath}/scans`} component={ScanTable} />
        <Route path={`${salePath}/salelots`} component={BusinessUserSaleLots} />
        <Redirect exact path={salePath} to={defaultRedirect} />
      </Switch>
    );
  }

  return (
    <>
      <Switch>
        <Route
          exact
          path={`${salePath}/overview`}
          component={getSaleTypeOverviewComponent(saleType)}
        />
        <Route
          exact
          path={`${salePath}/businesses-in-sale`}
          component={BusinessesInSaleTable}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureConsignments}
          path={`${salePath}/consignment-cards`}
          component={ConsignmentCardsView}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featurePens}
          path={`${salePath}/auction-pen-cards`}
          component={AuctionPenCardsView}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureBuyers}
          path={`${salePath}/buyer-cards`}
          component={BuyerCardsView}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureAttachments}
          path={`${salePath}/attachments`}
          component={Attachments}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureNominations}
          path={`${salePath}/nominations`}
          component={NominationTable}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureNominations}
          path={`${salePath}/nomination-cards`}
          component={NominationCards}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureConsignments}
          path={`${salePath}/consignments`}
          component={ConsignmentsTable}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectRoleCurrentDeployments}
          permissionRequired={DeploymentPermission.featureAuction}
          exact
          path={`${salePath}/firstround`}
          component={AuctionPenSale}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureScans}
          path={`${salePath}/eids`}
          component={ScanTable}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureEContracts}
          path={`${salePath}/econtracts`}
          component={EContractView}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureReports}
          path={`${salePath}/reports`}
          component={Reports}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureSaleLots}
          path={`${salePath}/salelots`}
          component={getSaleLotsTableComponent(saleType)}
        />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureSaleLots}
          path={`${salePath}/sale-lot-cards`}
          component={SaleLotCardsView}
        />
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featureMLASupplementaryData}
          path={`${salePath}/MLA`}
          component={MLAPenList}
        />
        <Route path={`${salePath}/take`} component={Take} />
        <Route path={`${salePath}/sell`} component={Sell} />
        <SecurityTrimmedRoute
          objectArraySelector={selectCurrentDeployments}
          permissionRequired={DeploymentPermission.featureScans}
          path={`${salePath}/scans`}
          component={ScanTable}
        />
        <Route path={`${salePath}/buyers`} component={Buyers} />
        <Route path={`${salePath}/vendors`} component={Vendors} />
        <Route path={`${salePath}/catalogue`} component={Catalogue} />
        {isLivestockAgent && (
          <Route exact path={`${salePath}/import`} component={SG8Importer} />
        )}
        {isLivestockAgent && (
          <Route
            exact
            path={`${salePath}/ap-import`}
            component={AuctionsPlusImporter}
          />
        )}
        {isLivestockAgent && (
          <Route
            exact
            path={`${salePath}/ex-agent-import`}
            component={ExternalAgentXmlImporter}
          />
        )}
        {isLivestockAgent && (
          <Route
            exact
            path={`${salePath}/csv-presale-import`}
            component={PreSaleCsvImporter}
          />
        )}
        {isLivestockAgent && (
          <Route
            exact
            path={getClearingSaleCsvImportRoute()}
            component={ClearingSalePreSaleCsvImporter}
          />
        )}
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featureWatcher}
          path={`${salePath}/watch`}
          component={SaleWatcher}
        />
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featureReceivalLots}
          path={`${salePath}/arrival-scanning`}
          component={ReceivalLotsTable}
        />
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featureReceivalLots}
          path={`${salePath}/arrival-scanning-cards`}
          component={ArrivalScanningView}
        />
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featurePenScanLots}
          path={`${salePath}/pen-scanning`}
          component={PenScanLotsTable}
        />
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featurePenScanLots}
          path={`${salePath}/pen-scanning-cards`}
          component={PenScanningView}
        />
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featureWeighLots}
          path={`${salePath}/weigh-scanning`}
          component={WeighScanLotsTable}
        />
        <SecurityTrimmedRoute
          objectSelector={getCurrentSaleyard}
          permissionRequired={SaleyardPermission.featureBidders}
          path={`${salePath}/bidder-cards`}
          component={RegisteredBiddersView}
        />
        <FeatureRoute
          roleDeploymentPermissions={[DeploymentPermission.featureBulkWeigh]}
          saleyardPermissions={[SaleyardPermission.featureBulkWeigh]}
          path={`${salePath}/weighbridge`}
          component={WeighbridgeSaleViewRoute}
        />
        {allowLivestockAgentNlisRoute ? (
          <Route path={`${salePath}/nlis`} component={NLISFiles} />
        ) : (
          <SecurityTrimmedRoute
            objectSelector={getCurrentSaleyard}
            permissionRequired={SaleyardPermission.featureNlis}
            path={`${salePath}/nlis`}
            component={NLISFiles}
          />
        )}
        <FeatureRoute
          roleDeploymentPermissions={[
            DeploymentPermission.featureBilling,
            DeploymentPermission.featureManualCharges,
            DeploymentPermission.featureBillingRun,
          ]}
          saleyardPermissions={[
            SaleyardPermission.featureBilling,
            SaleyardPermission.featureManualCharges,
            SaleyardPermission.featureBillingRun,
          ]}
          path={`${salePath}/billing`}
          component={BillingWorkFlow}
        />
        <Route
          exact
          path={`${salePath}/lot-sheet/:saleLotId/`}
          component={LotSheet}
        />
        <Route
          exact
          path={`${salePath}/placard/:saleLotId?/:round?/:agentId?`}
          component={RenderPlacardV1}
        />
        <Route
          exact
          path={`${salePath}/placardv2/:saleLotId?/:round?/:agentId?`}
          component={RenderPlacardV2}
        />
        <Route
          exact
          path={`${salePath}/placardv5/:saleLotId?/:round?/:agentId?`}
          component={RenderPlacardV5}
        />
        <Route
          exact
          path={`${salePath}/placardv3/:saleLotId?/:round?/:agentId?/A3`}
          component={RenderA3PlacardV3}
        />
        <Route
          exact
          path={`${salePath}/placardv3/:saleLotId?/:round?/:agentId?/A4`}
          component={RenderA4PlacardV3}
        />
        <Route
          exact
          path={`${salePath}/placardv4/:saleLotId?/:round?/:agentId?`}
          component={RenderPlacardV4}
        />
        <Route
          exact
          path={`${salePath}/sheepplacard/:saleLotId?/:round?/:agencyId?`}
          component={SheepPlacard}
        />
        <Route
          exact
          path={`${salePath}/sheeppenstorecard/:saleLotId?/:round?/:agencyId?`}
          component={SheepPenStoreCard}
        />
        {/* Send /saleyard/Ballarat/sale/1234 to overview (or reports) */}
        <Redirect exact path={salePath} to={defaultRedirect} />
      </Switch>
      <HashRoute
        component={EditSaleLotModal}
        hash={ModalTypes.EditSaleLot}
        componentProps={{ saleyardId, saleId }}
      />
      <HashRoute
        component={DeliverSaleLotModalAdapter}
        hash={ModalTypes.DeliverSaleLot}
      />
      <HashRoute
        component={DeliveryPenModalAdapter}
        hash={ModalTypes.DeliveryPen}
      />

      <HashRoute
        component={ScanToPenModalAdapter}
        hash={ModalTypes.ScanToPen}
      />
      <HashRoute
        component={SaleProgressDetailsDialog}
        hash={ModalTypes.SaleProgressOverview}
      />
      <HashRoute
        component={SplitSaleLotModal}
        hash={ModalTypes.SplitSaleLot}
        componentProps={{ saleyardId }}
      />

      <HashRoute
        component={SundryModalAdapter}
        hash={ModalTypes.ManualAdjustmentForm}
      />

      <HashRoute
        component={ManualAdjustmentTableModal}
        hash={ModalTypes.ManualAdjustmentTable}
      />

      <HashRoute component={MergeSaleLotModal} hash={ModalTypes.MergeSaleLot} />
      <HashRoute component={KillSheetModal} hash={ModalTypes.KillSheet} />
      <HashRoute
        component={SaleLotCarrierChargeModalAdapter}
        hash={ModalTypes.EditSaleLotCarrierCharge}
      />
      <HashRoute
        component={ConsignmentCarrierChargeModalAdapter}
        hash={ModalTypes.ConsignmentCarrierCharge}
      />
      <HashRoute
        component={SendVendorReportsModal}
        hash={ModalTypes.SendVendorReports}
      />

      <HashRoute
        component={UpdateBuyerModalAdapter}
        hash={ModalTypes.UpdateBuyer}
      />
      <StateModalAdapter
        component={EditConsignmentModal}
        hash={ModalTypes.Consignment}
        componentProps={{ saleId }}
      />
      <HashRoute
        component={EditBidderModalAdapter}
        hash={ModalTypes.EditBidder}
        componentProps={{ saleId, saleyardName }}
      />
      <HashRoute component={Scan} hash={ModalTypes.Scan} />
      <StateModalAdapter
        hash={ModalTypes.BusinessPayments}
        component={BusinessPaymentsModal}
      />
      <StateModalAdapter
        hash={ModalTypes.BuyerPaymentSummary}
        component={BuyerPaymentSummaryModal}
      />
      <StateModalAdapter
        hash={ModalTypes.AccountingDayRouting}
        component={AccountingDayRoutingModal}
      />
      <StateModalAdapter
        component={EditPaymentModal}
        hash={ModalTypes.EditPayment}
      />
      <HashRoute
        component={InlineCreateSaleLotModalAdapter}
        hash={ModalTypes.InlineCreateSaleLot}
      />
      <HashRoute component={CommentsModalAdapter} hash={ModalTypes.Comments} />
      <HashRoute
        component={DeliveryPenOwnersModalAdapter}
        hash={ModalTypes.DeliveryPenOwners}
      />
      <HashRoute
        component={EditCheckpointModalAdapter}
        hash={ModalTypes.EditCheckpoint}
      />
      <HashRoute component={SaleLotModalAdapter} hash={ModalTypes.SaleLot} />
      <HashRoute
        component={VendorSplitConsignmentModalAdapter}
        hash={ModalTypes.VendorSplitConsignment}
      />
      <HashRoute
        component={VendorSplitSaleLotModalAdapter}
        hash={ModalTypes.VendorSplitSaleLot}
      />

      <HashRoute
        component={EditPenScanLotModalAdapter}
        hash={ModalTypes.EditPenScanLot}
      />
      <HashRoute
        component={EditScanningPenModalAdapter}
        hash={ModalTypes.EditScanningPen}
      />
      <StateModalAdapter
        hash={ModalTypes.EditReceivalLot}
        component={EditReceivalLotModal}
      />
      <StateModalAdapter
        hash={ModalTypes.BulkUpdateSaleLot}
        component={BulkUpdateSaleLotsModal}
      />
      <StateModalAdapter
        hash={ModalTypes.BulkUpdateClearingSaleSaleLot}
        component={BulkUpdateClearingSaleSaleLotsModal}
      />
      <StateModalAdapter
        hash={ModalTypes.BulkUpdateBusiness}
        component={BulkUpdateBusinessModal}
      />
      <StateModalAdapter
        hash={ModalTypes.AllocatePenScanLots}
        component={AllocatePenScanLotsModal}
      />
      <StateModalAdapter
        hash={ModalTypes.ForceTransfer}
        component={ConfirmManualTransfer}
      />
      <StateModalAdapter
        hash={ModalTypes.ImportWeighLots}
        component={ImportWeighLotsModal}
      />
      <StateModalAdapter
        hash={ModalTypes.WeightSettings}
        component={WeightSettingsModal}
      />
      <StateModalAdapter
        hash={ModalTypes.DefaultBuyerWayProperty}
        component={UpdateDefaultPropertyModal}
      />
    </>
  );
}

SaleRoutes.propTypes = {
  saleId: PropTypes.number.isRequired,
  saleType: PropTypes.string.isRequired,
  saleyardName: PropTypes.string.isRequired,
  saleyardId: PropTypes.number.isRequired,
};
